import React from "react";
import { ChevronDown, FolderPlus, FolderUp, PenLine, Plus, Upload } from "lucide-react";

import { BrandButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

import type { LucideIcon } from "lucide-react";

export type AddKind = "files" | "folder-upload" | "note" | "folder";

/* The ways something new comes into a folder, in the New skill menu's look (DESIGN §15):
   an icon, a label and a line saying what it takes. */
export const ADD_WAYS: { kind: AddKind; Icon: LucideIcon; label: string; hint: string }[] = [
  { kind: "files", Icon: Upload, label: "Upload files", hint: "Up to 10 MB each" },
  { kind: "folder-upload", Icon: FolderUp, label: "Upload folder", hint: "Keeps the folders inside it" },
  { kind: "note", Icon: PenLine, label: "Write a note", hint: "Markdown the agents can read" },
  { kind: "folder", Icon: FolderPlus, label: "New folder", hint: "An empty folder to fill" },
];

export const menuCls = "p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu";
export const menuLabelCls = "px-2 pt-1.5 pb-1 text-[11.5px] font-medium text-[var(--text-tertiary)]";

/** The add items as menu entries, for this menu and the breadcrumb's. */
export function AddItems({ onAdd }: { onAdd: (kind: AddKind) => void }) {
  // Past Radix's close and focus restore, so a file picker or dialog opens cleanly
  const after = (kind: AddKind) => () => setTimeout(() => onAdd(kind), 0);
  return (
    <>
      {ADD_WAYS.map(({ kind, Icon, label, hint }) => (
        <DropdownMenuItem
          key={kind}
          onSelect={after(kind)}
          className="flex items-start gap-2.5 px-2 py-[7px] rounded-[8px] cursor-pointer focus:bg-[var(--accent-hover)]"
        >
          <Icon className="w-[14px] h-[14px] mt-[2px] shrink-0 text-[var(--text-secondary)]" strokeWidth={1.75} />
          <span className="min-w-0">
            <span className="block text-[13px] text-[var(--text-primary)]">{label}</span>
            <span className="block text-[12px] leading-[1.45] text-[var(--text-tertiary)]">{hint}</span>
          </span>
        </DropdownMenuItem>
      ))}
    </>
  );
}

/** New: adds to `place`, the folder (or base) that is open. */
export function KbAddMenu({ place, onAdd, className = "" }: {
  place: string;
  onAdd: (kind: AddKind) => void;
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BrandButton className={className}>
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          New
          <ChevronDown className="w-3 h-3 -mr-0.5 ml-auto opacity-70" strokeWidth={2} />
        </BrandButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={`w-[272px] ${menuCls}`}>
        <DropdownMenuLabel className={`${menuLabelCls} truncate`}>Add to {place}</DropdownMenuLabel>
        <AddItems onAdd={onAdd} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
